import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Grid,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Checkbox,
  Tooltip,
  CardHeader,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  LocationOn as LocationOnIcon,
  Save as SaveIcon,
  LocalShipping as LocalShippingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  QrCodeScanner,
  AddBox,
  Add as AddIcon,
  Remove as RemoveIcon,
  Settings as SettingsIcon,
  AllInbox as AllInboxIcon,
  CheckBox as CheckBoxIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
} from '@mui/icons-material';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import StatusBadge from 'src/components/TransferOrder/StatusBadge';
import Loader from 'src/components/Loader';
import {
  TransferOrder,
  TransferOrderStatus,
  TransferOrderRecord,
  PickedItemState,
} from 'src/types/TransferOrder.types';
import { TransferOrderService } from 'src/services/transfer-order-service';
import { toast } from 'react-toastify';
import { DataService } from 'src/services/data-service';

interface PickItemWithBox extends PickedItemState {
  selectedSourceBox?: string;
  selectedDestinationBox?: string;
  sourceBoxOptions?: string[];
  newBoxGenerated?: boolean;
  recordId: string;
  initialPickedQuantity: number;
}

interface BoxGroup {
  boxBarcode: string;
  items: PickItemWithBox[];
  totalAllocated: number;
  totalPicked: number;
  isExpanded: boolean;
  allSelected: boolean;
}

const PickOrder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const scannerRef = useRef<HTMLInputElement>(null);

  const [currentOrder, setCurrentOrder] = useState<TransferOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [pickedItems, setPickedItems] = useState<PickItemWithBox[]>([]);

  // Scanner state
  const [scannerInput, setScannerInput] = useState('');
  const [lastScannedBarcode, setLastScannedBarcode] = useState('');

  // Box scanning state
  const [scannedBox, setScannedBox] = useState<BoxGroup | null>(null);
  const [showBoxDialog, setShowBoxDialog] = useState(false);

  // Advanced dialog (optional)
  const [showAdvancedDialog, setShowAdvancedDialog] = useState(false);
  const [currentAdvancedItem, setCurrentAdvancedItem] = useState<PickItemWithBox | null>(null);
  const [sourceBoxInput, setSourceBoxInput] = useState('');
  const [destinationBoxInput, setDestinationBoxInput] = useState('');
  const [boxGenerationMode, setBoxGenerationMode] = useState<'existing' | 'generate' | 'none'>('none');
  const [deliveryAreaBoxes, setDeliveryAreaBoxes] = useState<Record<string, string>>({});

  // Grouping and expansion
  const [expandedBoxes, setExpandedBoxes] = useState<Set<string>>(new Set());
  const [expandedLooseItems, setExpandedLooseItems] = useState(false);

  const fetchOrder = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const order = await TransferOrderService.getById(id);
      setCurrentOrder(order);

      // Load delivery area boxes if delivery area is set
      if (order.DeliveryAreaStorageUnitId) {
        await loadDeliveryAreaBoxes(order.DeliveryAreaStorageUnitId);
      }
    } catch (error: any) {
      console.error('Failed to fetch transfer order:', error);
      const errorMessage = error?.message || 'Failed to load transfer order';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryAreaBoxes = async (storageUnitId: string) => {
    try {
      const response = await DataService.get(`api/Lov/${storageUnitId}/boxes`);
      if (response.ok) {
        const boxes = await response.json();
        setDeliveryAreaBoxes(boxes || {});
      }
    } catch (error) {
      console.error('Failed to load delivery area boxes:', error);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  // Initialize picked items state from order records
  useEffect(() => {
    if (currentOrder?.Records) {
      const items: PickItemWithBox[] = currentOrder.Records.map((record) => {
        const originalPicked = record.pickedQuantity ?? 0;

        return {
          barcode: record.barcode,
          allocatedQuantity: record.allocatedQuantity,
          pickedQuantity: originalPicked,          // what is currently picked
          sourceLocation: record.sourceStorageUnitName || 'Unknown',
          boxRefNumber: record.boxRefNumber,
          scannedAt: null,
          status:
            originalPicked === 0
              ? 'pending'
              : originalPicked < record.allocatedQuantity
              ? 'partial'
              : 'complete',
          sourceBoxOptions: record.boxRefNumber ? [record.boxRefNumber] : [],
          selectedSourceBox: record.boxRefNumber || '',
          selectedDestinationBox: '',
          recordId: record.id,
          initialPickedQuantity: originalPicked,   // store baseline
        };
      });
      setPickedItems(items);
    }
  }, [currentOrder]);


  // Auto-focus scanner on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      scannerRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Keyboard shortcut - Ctrl+I to focus scanner
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'i') {
        event.preventDefault();
        scannerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Group items by box
  const boxGroups = useMemo(() => {
    const groups = new Map<string, BoxGroup>();

    pickedItems.forEach(item => {
      if (item.boxRefNumber) {
        if (!groups.has(item.boxRefNumber)) {
          groups.set(item.boxRefNumber, {
            boxBarcode: item.boxRefNumber,
            items: [],
            totalAllocated: 0,
            totalPicked: 0,
            isExpanded: expandedBoxes.has(item.boxRefNumber),
            allSelected: false,
          });
        }
        const group = groups.get(item.boxRefNumber)!;
        group.items.push(item);
        group.totalAllocated += item.allocatedQuantity;
        group.totalPicked += item.pickedQuantity;
      }
    });

    return Array.from(groups.values());
  }, [pickedItems, expandedBoxes]);

  // Loose items (not in any box)
  const looseItems = useMemo(() => {
    return pickedItems.filter(item => !item.boxRefNumber);
  }, [pickedItems]);

  const toggleBoxExpansion = (boxBarcode: string) => {
    setExpandedBoxes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(boxBarcode)) {
        newSet.delete(boxBarcode);
      } else {
        newSet.add(boxBarcode);
      }
      return newSet;
    });
  };

  // Handle scanner input
  const handleScannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScannerInput(e.target.value);
  };

  const handleScannerKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleScan(scannerInput.trim());
    }
  };

  const handleScan = async (barcode: string) => {
    if (!barcode) return;

    setLastScannedBarcode(barcode);

    // Check if it's a box barcode
    const boxGroup = boxGroups.find(group => group.boxBarcode === barcode);

    if (boxGroup) {
      // Box scanned - show box dialog
      setScannedBox({
        ...boxGroup,
        allSelected: true, // Default to all selected
      });
      setShowBoxDialog(true);
      setScannerInput('');
      return;
    }

    // Individual item scanned - auto-increment
    const item = pickedItems.find(i => i.barcode === barcode);

    if (!item) {
      dispatch(showAlert({
        message: `Barcode ${barcode} not found in this order`,
        type: 'error',
      }));
      setScannerInput('');
      return;
    }

    // Check if already fully picked
    if (item.status === 'complete') {
      dispatch(showAlert({
        message: `${barcode} already fully picked`,
        type: 'warning',
      }));
      setScannerInput('');
      return;
    }

    // Auto-increment by 1
    const remaining = item.allocatedQuantity - item.pickedQuantity;
    const increment = Math.min(1, remaining);

    handleIncrementItem(barcode, increment);
    setScannerInput('');
  };

  const handleIncrementItem = (barcode: string, amount: number) => {
    setPickedItems(prev => prev.map(item => {
      if (item.barcode === barcode) {
        const newPickedQty = Math.min(item.allocatedQuantity, item.pickedQuantity + amount);
        const newStatus: 'pending' | 'partial' | 'complete' =
          newPickedQty === 0 ? 'pending' :
          newPickedQty < item.allocatedQuantity ? 'partial' : 'complete';

        dispatch(showAlert({
          message: `${barcode}: ${newPickedQty}/${item.allocatedQuantity}`,
          type: 'success',
        }));

        return {
          ...item,
          pickedQuantity: newPickedQty,
          scannedAt: new Date().toISOString(),
          status: newStatus,
        };
      }
      return item;
    }));
  };

  const handleDecrementItem = (barcode: string, amount: number) => {
    setPickedItems(prev => prev.map(item => {
      if (item.barcode === barcode) {
        const newPickedQty = Math.max(0, item.pickedQuantity - amount);
        const newStatus: 'pending' | 'partial' | 'complete' =
          newPickedQty === 0 ? 'pending' :
          newPickedQty < item.allocatedQuantity ? 'partial' : 'complete';

        return {
          ...item,
          pickedQuantity: newPickedQty,
          status: newStatus,
        };
      }
      return item;
    }));
  };

  const handleSetQuantity = (barcode: string, quantity: number) => {
    setPickedItems(prev => prev.map(item => {
      if (item.barcode === barcode) {
        const newPickedQty = Math.max(0, Math.min(item.allocatedQuantity, quantity));
        const newStatus: 'pending' | 'partial' | 'complete' =
          newPickedQty === 0 ? 'pending' :
          newPickedQty < item.allocatedQuantity ? 'partial' : 'complete';

        return {
          ...item,
          pickedQuantity: newPickedQty,
          scannedAt: newPickedQty > 0 ? new Date().toISOString() : null,
          status: newStatus,
        };
      }
      return item;
    }));
  };

  const handlePickAll = (barcode: string) => {
    const item = pickedItems.find(i => i.barcode === barcode);
    if (item) {
      handleSetQuantity(barcode, item.allocatedQuantity);
      dispatch(showAlert({
        message: `Picked all ${item.allocatedQuantity} units of ${barcode}`,
        type: 'success',
      }));
    }
  };

  const handleClearPicked = (barcode: string) => {
    handleSetQuantity(barcode, 0);
  };

  // Box dialog handlers
  const handleToggleBoxItem = (barcode: string) => {
    if (!scannedBox) return;

    setScannedBox(prev => {
      if (!prev) return prev;

      const item = prev.items.find(i => i.barcode === barcode);
      if (!item) return prev;

      // Toggle between 0 and allocated quantity
      const newQty = item.pickedQuantity === item.allocatedQuantity ? 0 : item.allocatedQuantity;

      const updatedItems = prev.items.map(i =>
        i.barcode === barcode
          ? { ...i, pickedQuantity: newQty }
          : i
      );

      const allSelected = updatedItems.every(i => i.pickedQuantity === i.allocatedQuantity);

      return {
        ...prev,
        items: updatedItems,
        allSelected,
      };
    });
  };

  const handleToggleAllBoxItems = () => {
    if (!scannedBox) return;

    setScannedBox(prev => {
      if (!prev) return prev;

      const newAllSelected = !prev.allSelected;
      const updatedItems = prev.items.map(item => ({
        ...item,
        pickedQuantity: newAllSelected ? item.allocatedQuantity : 0,
      }));

      return {
        ...prev,
        items: updatedItems,
        allSelected: newAllSelected,
      };
    });
  };

  const handleConfirmBoxPick = () => {
    if (!scannedBox) return;

    // Update all items in the box
    setPickedItems(prev => prev.map(item => {
      const boxItem = scannedBox.items.find(bi => bi.barcode === item.barcode);
      if (boxItem) {
        const newStatus: 'pending' | 'partial' | 'complete' =
          boxItem.pickedQuantity === 0 ? 'pending' :
          boxItem.pickedQuantity < boxItem.allocatedQuantity ? 'partial' : 'complete';

        return {
          ...item,
          pickedQuantity: boxItem.pickedQuantity,
          scannedAt: boxItem.pickedQuantity > 0 ? new Date().toISOString() : null,
          status: newStatus,
        };
      }
      return item;
    }));

    const totalPicked = scannedBox.items.reduce((sum, item) => sum + item.pickedQuantity, 0);

    dispatch(showAlert({
      message: `Picked ${totalPicked} units from box ${scannedBox.boxBarcode}`,
      type: 'success',
    }));

    setShowBoxDialog(false);
    setScannedBox(null);

    // Refocus scanner
    setTimeout(() => scannerRef.current?.focus(), 100);
  };

  // Advanced dialog handlers
  const handleOpenAdvanced = (item: PickItemWithBox) => {
    setCurrentAdvancedItem(item);
    setSourceBoxInput(item.selectedSourceBox || '');
    setDestinationBoxInput(item.selectedDestinationBox || '');
    setBoxGenerationMode('none');
    setShowAdvancedDialog(true);
  };

  const generateNewBoxBarcode = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `BOX-${currentOrder?.OrderName || 'GEN'}-${timestamp}-${random}`;
  };

  const handleConfirmAdvanced = () => {
    if (!currentAdvancedItem) return;

    let finalDestinationBox = destinationBoxInput;
    if (boxGenerationMode === 'generate') {
      finalDestinationBox = generateNewBoxBarcode();
    }

    setPickedItems(prev => prev.map(item => {
      if (item.barcode === currentAdvancedItem.barcode) {
        return {
          ...item,
          selectedSourceBox: sourceBoxInput || item.selectedSourceBox,
          selectedDestinationBox: finalDestinationBox || item.selectedDestinationBox,
          newBoxGenerated: boxGenerationMode === 'generate',
        };
      }
      return item;
    }));

    dispatch(showAlert({
      message: `Updated box settings for ${currentAdvancedItem.barcode}`,
      type: 'success',
    }));

    setShowAdvancedDialog(false);
    setCurrentAdvancedItem(null);
  };

  const handleSaveProgress = async () => {
    if (!currentOrder) return;

  const itemsToPick = pickedItems
    .filter((item) => item.pickedQuantity > item.initialPickedQuantity)
    .map((item) => ({
      barcode: item.barcode,
      quantity: item.pickedQuantity - item.initialPickedQuantity,
      sourceBoxRefNumber: item.selectedSourceBox || undefined,
      destinationBoxRefNumber: item.selectedDestinationBox || undefined,
    }));

    if (itemsToPick.length === 0) {
      dispatch(showAlert({ message: 'No items picked yet', type: 'warning' }));
      toast.warning('No items picked yet');
      return;
    }

    setOperationLoading(true);
    try {
      await TransferOrderService.pick({
        transferOrderId: currentOrder.Id,
        items: itemsToPick,
        notes: 'Partial picking - progress saved',
      });

      dispatch(
        showAlert({
          message: 'Progress saved successfully',
          type: 'success',
        })
      );
      toast.success('Progress saved successfully');

      // Refresh order
      await fetchOrder();
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to save progress';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleCompletePicking = async () => {
    if (!currentOrder) return;

    const itemsToPick = pickedItems
      .filter((item) => item.pickedQuantity > item.initialPickedQuantity)
      .map((item) => ({
        barcode: item.barcode,
        quantity: item.pickedQuantity - item.initialPickedQuantity,
        sourceBoxRefNumber: item.selectedSourceBox || undefined,
        destinationBoxRefNumber: item.selectedDestinationBox || undefined,
      }));

    if (itemsToPick.length === 0) {
      dispatch(showAlert({ message: 'No items picked yet', type: 'warning' }));
      toast.warning('No items picked yet');
      return;
    }

    setOperationLoading(true);
    try {
      const result = await TransferOrderService.pick({
        transferOrderId: currentOrder.Id,
        items: itemsToPick,
        notes: 'Picking completed',
      });

      dispatch(
        showAlert({
          message: result.message || 'Picking completed successfully',
          type: 'success',
        })
      );
      toast.success(result.message || 'Picking completed successfully');

      // Navigate based on new status
      if (result.newOrderStatus === TransferOrderStatus.Ready) {
        navigate(`/transfer-orders/${id}/ship`);
      } else {
        navigate(`/transfer-orders/${id}`);
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to complete picking';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setOperationLoading(false);
    }
  };

  const progress = useMemo(() => {
    if (pickedItems.length === 0) return { picked: 0, total: 0, percentage: 0 };

    const picked = pickedItems.filter((item) => item.status === 'complete').length;
    const total = pickedItems.length;
    const percentage = (picked / total) * 100;

    return { picked, total, percentage };
  }, [pickedItems]);

  const totalQuantity = useMemo(() => {
    const allocated = pickedItems.reduce((sum, item) => sum + item.allocatedQuantity, 0);
    const picked = pickedItems.reduce((sum, item) => sum + item.pickedQuantity, 0);
    return { allocated, picked, percentage: allocated > 0 ? (picked / allocated) * 100 : 0 };
  }, [pickedItems]);

  const nextItemToPick = useMemo(() => {
    return pickedItems.find((item) => item.status !== 'complete');
  }, [pickedItems]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loader />
      </Box>
    );
  }

  if (!currentOrder) {
    return (
      <Box>
        <Alert severity="error">Transfer order not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/transfer-order')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  const renderItemRow = (item: PickItemWithBox, isInBox: boolean = false) => (
    <TableRow key={item.barcode} sx={{ bgcolor: isInBox ? 'action.hover' : 'inherit' }}>
      <TableCell sx={{ pl: isInBox ? 4 : 2 }}>
        <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
          {item.barcode}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {item.sourceLocation}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={item.allocatedQuantity}
          size="small"
          variant="outlined"
          sx={{ minWidth: 50 }}
        />
      </TableCell>
      <TableCell align="center">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          <IconButton
            size="small"
            onClick={() => handleDecrementItem(item.barcode, 1)}
            disabled={item.pickedQuantity === 0}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <TextField
            value={item.pickedQuantity}
            onChange={(e) => handleSetQuantity(item.barcode, parseInt(e.target.value) || 0)}
            type="number"
            size="small"
            inputProps={{ min: 0, max: item.allocatedQuantity, style: { textAlign: 'center' } }}
            sx={{ width: 80 }}
          />
          <IconButton
            size="small"
            onClick={() => handleIncrementItem(item.barcode, 1)}
            disabled={item.pickedQuantity >= item.allocatedQuantity}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>
      </TableCell>
      <TableCell align="center">
        <LinearProgress
          variant="determinate"
          value={(item.pickedQuantity / item.allocatedQuantity) * 100}
          sx={{ height: 6, borderRadius: 1, minWidth: 80 }}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={item.status === 'complete' ? 'Complete' : item.status === 'partial' ? 'Partial' : 'Pending'}
          color={item.status === 'complete' ? 'success' : item.status === 'partial' ? 'warning' : 'default'}
          size="small"
          icon={item.status === 'complete' ? <CheckCircleIcon /> : <InventoryIcon />}
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Pick All">
            <IconButton
              size="small"
              onClick={() => handlePickAll(item.barcode)}
              disabled={item.status === 'complete'}
              color="primary"
            >
              <CheckBoxIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear">
            <IconButton
              size="small"
              onClick={() => handleClearPicked(item.barcode)}
              disabled={item.pickedQuantity === 0}
              color="error"
            >
              <IndeterminateCheckBoxIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Advanced Settings">
            <IconButton
              size="small"
              onClick={() => handleOpenAdvanced(item)}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate(`/transfer-orders/${id}`)}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h4" fontWeight={700}>
            Pick Order: {currentOrder.OrderName || `#${currentOrder.Id.slice(0, 8)}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Client: {currentOrder.ClientName}
          </Typography>
        </Box>
        <StatusBadge status={currentOrder.Status} />
      </Stack>

      {/* Progress Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Item Progress
              </Typography>
              <Stack spacing={1}>
                <Typography variant="h5" fontWeight={700}>
                  {progress.picked} / {progress.total}
                </Typography>
                <LinearProgress variant="determinate" value={progress.percentage} sx={{ height: 8, borderRadius: 1 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Quantity Progress
              </Typography>
              <Stack spacing={1}>
                <Typography variant="h5" fontWeight={700}>
                  {totalQuantity.picked} / {totalQuantity.allocated}
                </Typography>
                <LinearProgress variant="determinate" value={totalQuantity.percentage} sx={{ height: 8, borderRadius: 1 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Boxes / Loose
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  label={`${boxGroups.length} Boxes`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
                <Chip
                  label={`${looseItems.length} Loose`}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Next Item to Pick */}
      {nextItemToPick && (
        <Alert severity="info" icon={<LocationOnIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            Next: {nextItemToPick.barcode} ({nextItemToPick.allocatedQuantity - nextItemToPick.pickedQuantity} units from{' '}
            {nextItemToPick.sourceLocation})
          </Typography>
        </Alert>
      )}

      {/* Barcode Scanner */}
      <Card sx={{ mb: 3, bgcolor: 'primary.50', borderColor: 'primary.main' }} variant="outlined">
        <CardHeader
          avatar={<QrCodeScanner color="primary" />}
          title="Barcode Scanner"
          subheader="Scan box to pick all items, or scan individual items to auto-increment (Ctrl+I to focus)"
          sx={{
            p: 2,
            '& .MuiCardHeader-title': { fontSize: '1rem', fontWeight: 600 },
            '& .MuiCardHeader-subheader': { fontSize: '0.8rem' },
          }}
        />
        <Divider />
        <CardContent sx={{ p: 2 }}>
          <TextField
            inputRef={scannerRef}
            fullWidth
            size="small"
            placeholder="Focus here and scan barcode..."
            value={scannerInput}
            onChange={handleScannerChange}
            onKeyPress={handleScannerKeyPress}
            autoFocus
            InputProps={{
              endAdornment: lastScannedBarcode && (
                <Chip
                  label={`Last: ${lastScannedBarcode}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Box Groups */}
      {boxGroups.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AllInboxIcon />
              Boxed Items ({boxGroups.length} boxes)
            </Typography>
            <Stack spacing={1}>
              {boxGroups.map((box) => (
                <Card key={box.boxBarcode} variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <IconButton size="small" onClick={() => toggleBoxExpansion(box.boxBarcode)}>
                        {expandedBoxes.has(box.boxBarcode) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                          📦 {box.boxBarcode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {box.items.length} items • {box.totalPicked}/{box.totalAllocated} units
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(box.totalPicked / box.totalAllocated) * 100}
                        sx={{ width: 150, height: 6, borderRadius: 1 }}
                      />
                      <Chip
                        label={box.totalPicked === box.totalAllocated ? 'Complete' : 'In Progress'}
                        color={box.totalPicked === box.totalAllocated ? 'success' : 'warning'}
                        size="small"
                      />
                    </Stack>
                    <Collapse in={expandedBoxes.has(box.boxBarcode)}>
                      <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Barcode / Source</TableCell>
                              <TableCell align="center">Allocated</TableCell>
                              <TableCell align="center">Picked</TableCell>
                              <TableCell align="center">Progress</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {box.items.map((item) => renderItemRow(item, true))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Loose Items */}
      {looseItems.length > 0 && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InventoryIcon />
                Independent Items ({looseItems.length})
              </Typography>
              <IconButton size="small" onClick={() => setExpandedLooseItems(!expandedLooseItems)}>
                {expandedLooseItems ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>
            <Collapse in={expandedLooseItems} timeout={0}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Barcode / Source</TableCell>
                      <TableCell align="center">Allocated</TableCell>
                      <TableCell align="center">Picked</TableCell>
                      <TableCell align="center">Progress</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {looseItems.map((item) => renderItemRow(item, false))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => navigate(`/transfer-orders/${id}`)} disabled={operationLoading}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSaveProgress}
          disabled={operationLoading || pickedItems.every((item) => item.pickedQuantity === 0)}
        >
          Save Progress
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<LocalShippingIcon />}
          onClick={handleCompletePicking}
          disabled={operationLoading || pickedItems.every((item) => item.pickedQuantity === 0)}
        >
          Complete Picking
        </Button>
      </Stack>

      {/* Box Pick Dialog */}
      <Dialog open={showBoxDialog} onClose={() => setShowBoxDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">📦 Box: {scannedBox?.boxBarcode}</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={scannedBox?.allSelected ? <IndeterminateCheckBoxIcon /> : <CheckBoxIcon />}
              onClick={handleToggleAllBoxItems}
            >
              {scannedBox?.allSelected ? 'Deselect All' : 'Select All'}
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              {scannedBox?.allSelected
                ? `All items selected • Total: ${scannedBox.items.reduce((sum, i) => sum + i.allocatedQuantity, 0)} units`
                : `${scannedBox?.items.filter(i => i.pickedQuantity > 0).length || 0} items selected • Total: ${scannedBox?.items.reduce((sum, i) => sum + i.pickedQuantity, 0) || 0} units`}
            </Alert>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Pick</TableCell>
                    <TableCell>Barcode</TableCell>
                    <TableCell align="center">Allocated</TableCell>
                    <TableCell align="center">To Pick</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scannedBox?.items.map((item) => (
                    <TableRow key={item.barcode}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={item.pickedQuantity === item.allocatedQuantity}
                          onChange={() => handleToggleBoxItem(item.barcode)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {item.barcode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.sourceLocation}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={item.allocatedQuantity} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Badge
                          badgeContent={item.pickedQuantity}
                          color={item.pickedQuantity === item.allocatedQuantity ? 'success' : 'warning'}
                        >
                          <Chip
                            label={item.pickedQuantity === item.allocatedQuantity ? 'All' : 'None'}
                            size="small"
                            color={item.pickedQuantity === item.allocatedQuantity ? 'success' : 'default'}
                          />
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBoxDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmBoxPick} variant="contained" color="primary">
            Confirm Pick ({scannedBox?.items.reduce((sum, i) => sum + i.pickedQuantity, 0) || 0} units)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advanced Dialog (Optional) */}
      <Dialog open={showAdvancedDialog} onClose={() => setShowAdvancedDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Advanced Settings: {currentAdvancedItem?.barcode}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Source Box (Optional)
            </Typography>
            <TextField
              label="Source Box Reference"
              value={sourceBoxInput}
              onChange={(e) => setSourceBoxInput(e.target.value)}
              placeholder={currentAdvancedItem?.boxRefNumber || 'Independent (no box)'}
              fullWidth
              InputProps={{
                startAdornment: <QrCodeScanner sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />

            <Divider />

            <Typography variant="subtitle2" color="text.secondary">
              Destination Box
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Box Mode</InputLabel>
              <Select value={boxGenerationMode} label="Box Mode" onChange={(e) => setBoxGenerationMode(e.target.value as any)}>
                <MenuItem value="none">No Box (Independent)</MenuItem>
                <MenuItem value="existing">Use Existing Box</MenuItem>
                <MenuItem value="generate">Generate New Box</MenuItem>
              </Select>
            </FormControl>

            {boxGenerationMode === 'existing' && (
              <>
                {Object.keys(deliveryAreaBoxes).length > 0 ? (
                  <FormControl fullWidth>
                    <InputLabel>Select Box</InputLabel>
                    <Select value={destinationBoxInput} label="Select Box" onChange={(e) => setDestinationBoxInput(e.target.value)}>
                      {Object.entries(deliveryAreaBoxes).map(([boxId, boxName]) => (
                        <MenuItem key={boxId} value={boxId}>
                          {boxName as string}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="Destination Box Reference"
                    value={destinationBoxInput}
                    onChange={(e) => setDestinationBoxInput(e.target.value)}
                    placeholder="Scan or enter box barcode"
                    fullWidth
                    InputProps={{
                      startAdornment: <QrCodeScanner sx={{ mr: 1, color: 'action.active' }} />,
                    }}
                  />
                )}
              </>
            )}

            {boxGenerationMode === 'generate' && (
              <Alert severity="success" icon={<AddBox />}>
                A new box barcode will be generated for this item
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAdvancedDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmAdvanced} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PickOrder;
